function extractMessages(errors) {
  if (Array.isArray(errors)) {
    return errors
      .map((item) => (item && typeof item === "object" ? item.message : item))
      .filter(Boolean);
  }
  if (errors && typeof errors === "object") {
    return Object.values(errors).flat().filter(Boolean);
  }
  return [];
}

export function getErrorMessage(error, fallback) {
  const responseData = error.response && error.response.data;
  if (!responseData) return fallback;
  if (responseData.message) return responseData.message;

  const errors = responseData.errors || responseData;
  return extractMessages(errors).join(" ") || fallback;
}

function normalize(s) {
  return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

export function searchMunicipios(municipios, query, limit = 50) {
  const search = normalize(query || "");
  if (search.length < 2) return [];
  return municipios
    .filter((m) => normalize(m.nome).includes(search))
    .slice(0, limit);
}

// Erros 422 novos ligados a IBS/CBS e totTrib, retornados por POST /company/nfse/issue.
const NFSE_ISSUE_ERROR_META = {
  E_NFSE_NBS_REQUIRED: {
    message:
      "O serviço selecionado está sem o código NBS. Complete o cadastro do serviço para emitir.",
    action: "service",
  },
  E_NFSE_IBSCBS_INCOMPLETE: {
    message:
      "O serviço selecionado está sem o indicador de operação e/ou a classificação tributária (IBS/CBS). Complete o cadastro do serviço para emitir.",
    action: "service",
  },
  E_NFSE_CONFIG_INCOMPLETE: {
    message:
      "A configuração fiscal da NFS-e está incompleta. Informe o regime de apuração antes de emitir.",
    action: "config",
  },
  E_NFSE_CCLASSTRIB_UNSUPPORTED: {
    message:
      "A classificação tributária IBS/CBS deste serviço ainda não é suportada na emissão. Escolha outro serviço ou entre em contato com o suporte.",
    action: null,
    preferServerMessage: true,
  },
  E_NFSE_IMOVEL_REQUIRED: {
    message:
      "Este serviço exige informações de obra/imóvel, ainda não suportadas na emissão. Não é possível emitir esta nota hoje.",
    action: null,
    preferServerMessage: true,
  },
  E_NFSE_TOTTRIB_MISSING: {
    message: null,
    action: null,
    preferServerMessage: true,
  },
  E_NFSE_TOTTRIB_LEGACY_FIELD: {
    message:
      "O formato da alíquota total de tributos mudou. Recarregue a página e emita novamente.",
    action: null,
  },
  // Imunidade e não incidência exigem reg_esp_trib "0" e ISSQN não retido.
  E_NFSE_REGESPTRIB_CONFLICT: {
    message:
      "A tributação selecionada exige que a empresa não tenha regime especial de tributação.",
    action: "config",
    preferServerMessage: true,
  },
  E_NFSE_TPRET_CONFLICT: {
    message:
      "A tributação selecionada não admite retenção — selecione \"Não retido\".",
    action: null,
    preferServerMessage: true,
  },
  E_NFSE_TPIMUNIDADE_REQUIRED: {
    message: "Selecione o tipo de imunidade.",
    action: null,
    preferServerMessage: true,
  },
  E_NFSE_TPIMUNIDADE_NOT_ALLOWED: {
    message: "O tipo de imunidade só pode ser informado quando a tributação for imunidade.",
    action: null,
    preferServerMessage: true,
  },
  // Exportação de serviço e partes no exterior exigem o grupo comExt.
  E_NFSE_COMEXT_REQUIRED: {
    message:
      "Preencha os dados de comércio exterior — obrigatórios na exportação de serviço e quando o tomador está no exterior.",
    action: null,
    preferServerMessage: true,
  },
  E_NFSE_MOVTEMPBENS_CONFLICT: {
    message:
      "O número da declaração informado não corresponde ao movimento temporário de bens selecionado.",
    action: null,
    preferServerMessage: true,
  },
  E_NFSE_CPAISRESULT_NOT_ALLOWED: {
    message:
      "O país do resultado só pode ser informado quando a tributação for exportação de serviço.",
    action: null,
    preferServerMessage: true,
  },
  E_NFSE_TAKER_ID_CONFLICT: {
    message:
      "Informe exatamente uma identificação do tomador: CPF/CNPJ, NIF ou o motivo de ausência de NIF.",
    action: null,
    preferServerMessage: true,
  },
  E_NFSE_TAKER_ADDRESS_CONFLICT: {
    message:
      "O endereço do tomador mistura campos nacionais e do exterior — informe apenas um dos dois.",
    action: null,
    preferServerMessage: true,
  },
  E_NFSE_NIF_REQUIRED: {
    message:
      "Endereço no exterior exige NIF ou o motivo de ausência de NIF — CPF/CNPJ não identifica alguém no exterior.",
    action: null,
    preferServerMessage: true,
  },
};

// Domínio oficial da NFS-e Nacional para reg_esp_trib (regime especial de
// tributação, concedido pelo município). Não confundir com o domínio municipal
// antigo (ABRASF), que tinha rótulos diferentes e não incluía o "7".
export const REG_ESP_TRIB_OPTIONS = [
  { value: "0", label: "0 - Nenhum" },
  { value: "1", label: "1 - Ato Cooperado (Cooperativa)" },
  { value: "2", label: "2 - Estimativa" },
  { value: "3", label: "3 - Microempresa Municipal" },
  { value: "4", label: "4 - Notário ou Registrador" },
  { value: "5", label: "5 - Profissional Autônomo" },
  { value: "6", label: "6 - Sociedade de Profissionais" },
  { value: "7", label: "7 - Específicos Municipais" },
];

export const REG_ESP_TRIB_VALUES = REG_ESP_TRIB_OPTIONS.map((o) => o.value);

export const REG_ESP_TRIB_LOCKED_MESSAGE =
  "MEI e empresas com apuração do ISSQN pelo Simples Nacional não podem ter regime especial de tributação.";

// A NFS-e Nacional exige reg_esp_trib = "0" para MEI e para ME/EPP com
// apuração do ISSQN pelo Simples Nacional (reg_ap_trib_sn = "1").
export function isRegEspTribLocked(opSimpNac, regApTribSN) {
  return String(opSimpNac) === "2" || String(regApTribSN) === "1";
}

function legacyIssueErrorMessage(data) {
  if (data.errors && data.errors.length) {
    return extractMessages(data.errors).join("; ");
  }
  return (
    (data.response_json && data.response_json.motivo) ||
    data.last_error ||
    data.lastError ||
    data.message ||
    ""
  );
}

// Resolve um erro de POST /company/nfse/issue em { code, message, action }.
// action: "service" | "config" | null — indica o CTA que a tela de emissão deve oferecer.
export function resolveIssueError(error, fallback) {
  const data = (error.response && error.response.data) || {};
  const meta = data.code ? NFSE_ISSUE_ERROR_META[data.code] : null;

  if (!meta) {
    return { code: data.code || null, message: legacyIssueErrorMessage(data) || fallback, action: null };
  }

  const message = (meta.preferServerMessage && data.message) || meta.message || legacyIssueErrorMessage(data) || fallback;
  return { code: data.code, message, action: meta.action };
}

// Constrói o payload de POST/PUT /company/nfse/services a partir do estado do formulário.
export function buildNfseServicePayload({ model, classification, nbs, indOp, classTrib }) {
  const p = {
    name: String(model.name || "").trim(),
    default_description: String(model.default_description || "").trim(),
    nfse_service_classification_id: classification ? classification.id : null,
    nfse_classification_nbs_id: nbs ? nbs.id : null,
    nfse_classification_ind_op_id: indOp ? indOp.id : null,
    nfse_classification_class_trib_id: classTrib ? classTrib.id : null,
  };
  const cnae = String(model.cnae || "").trim();
  if (cnae) p.cnae = cnae;
  if (model.default_price !== "" && model.default_price !== null) {
    p.default_price = Number(model.default_price);
  }
  return p;
}

// Retorna a mensagem de erro (string) ou null quando o payload é válido.
export function validateNfseServicePayload(payload) {
  if (!payload.name) return "Informe o nome do serviço.";
  if (!payload.default_description) return "Informe a descrição padrão.";
  if (!payload.nfse_service_classification_id) return "Selecione a classificação fiscal.";
  if (!payload.nfse_classification_nbs_id) return "Selecione o código NBS.";
  return null;
}

// cIndOp e cClassTrib (IBS/CBS) são opcionais até 2027 — não entram na
// checagem de "cadastro completo". NBS não é dado de IBS/CBS e é sempre
// obrigatório.
const SERVICE_FISCAL_FIELDS = [
  { id: "nfse_service_classification_id", relation: "classification", label: "classificação fiscal" },
  { id: "nfse_classification_nbs_id", relation: "nbs", label: "código NBS" },
];

// Lista os campos fiscais que faltam para um serviço poder ser usado na emissão.
export function nfseServiceMissingFields(service) {
  if (!service) return [];
  return SERVICE_FISCAL_FIELDS
    .filter((f) => !service[f.id] && !service[f.relation])
    .map((f) => f.label);
}

export function isNfseServiceIncomplete(service) {
  return nfseServiceMissingFields(service).length > 0;
}
